import {
  convertEventSelectToTarget,
  DataTable,
  formatDataToLabelValue,
  FormFeedBack,
  MainButton,
  ModalConfirmAction,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import {
  AddCourseToEntityUseCaseInterface,
  CourseListUseCaseInterface,
  GetAllCoursesFromEntityUseCaseInterface,
  GetCourseSettingsFromEntityUseCaseInterface,
  UnlinkCourseFromEntityUseCaseInterface,
} from 'domain/usecases';
import { useFormik } from 'formik';
import { notifyError, notifySuccess } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { makeCoursesFromEntityListColumns } from 'presentation/app/modules/Entities/common/columns/entity-settings/courses-from-entity-list-columns';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  clearCoursesFromEntityListData,
  loadCoursesFromEntityListData,
} from 'presentation/config/store/entities/entities-slice';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Card, CardBody, Col, Form, Row } from 'reactstrap';
import * as Yup from 'yup';

import { ModalCourseSettings } from './components/modal-course-settings';

interface IProps {
  entityId: string;
}

export const CoursesTabContent = ({ entityId }: IProps) => {
  // Hooks
  const { courseService, entitiesService } = useService();
  const dispatch = useDispatch();

  // States
  const { coursesFromEntityListData } = useAppSelector(
    (state) => state.Entities,
  );
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedRelationshipId, setSelectedRelationshipId] = useState<
    number | null
  >(null);
  const [courseSettings, setCourseSettings] =
    useState<GetCourseSettingsFromEntityUseCaseInterface.Result | null>(null);
  const [loadingCourseSettingsId, setLoadingCourseSettingsId] = useState<
    number | null
  >(null);
  const [coursesSelectOptions, setCoursesSelectOptions] = useState<
    CourseListUseCaseInterface.Result[]
  >([]);
  const selectedRelationshipIdRef = useRef(null);
  const [requestGetAllCoursesStatus, setRequestGetAllCoursesStatus] = useState(
    STATUSPAGE.DONE,
  );
  const [
    requestGetAllCoursesFromEntityStatus,
    setRequestGetAllCoursesFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);
  const [requestAddCourseToEntityStatus, setRequestAddCourseToEntityStatus] =
    useState(STATUSPAGE.DONE);
  const [
    requestUnlinkCourseFromEntityStatus,
    setRequestUnlinkCourseFromEntityStatus,
  ] = useState(STATUSPAGE.DONE);

  const noDataConfig = {
    noDataTitle: 'Nenhum curso vinculado',
    noDataDescription:
      'Nenhum curso foi vinculado a esta organização até o momento.',
  };

  // Methods
  const toggleUnlinkModal = useCallback(() => {
    setIsUnlinkModalOpen((prevState) => !prevState);
  }, []);

  const toggleSettingsModal = useCallback(() => {
    setIsSettingsModalOpen((prevState) => !prevState);
  }, []);

  const requestGetCourseSettingsFromEntity = useCallback(
    async (params: GetCourseSettingsFromEntityUseCaseInterface.Params) => {
      setLoadingCourseSettingsId(params.id);
      await entitiesService
        .getCourseSettingsFromEntity(params)
        .then((res) => {
          setCourseSettings(res);
          setSelectedRelationshipId(params.id);
          setIsSettingsModalOpen(true);
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setLoadingCourseSettingsId(null);
        });
    },
    [],
  );

  const requestGetAllCourses = useCallback(async () => {
    setRequestGetAllCoursesStatus(STATUSPAGE.LOADING);
    await courseService
      .courseList()
      .then((res) => setCoursesSelectOptions(res))
      .catch((err) => {})
      .finally(() => {
        setRequestGetAllCoursesStatus(STATUSPAGE.DONE);
      });
  }, []);

  const requestGetAllCoursesFromEntity = useCallback(
    async (params: GetAllCoursesFromEntityUseCaseInterface.Params) => {
      setRequestGetAllCoursesFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .getAllCoursesFromEntity(params)
        .then((res) => {
          dispatch(loadCoursesFromEntityListData(res));
        })
        .catch((err) => {
          notifyError(err.message);
          dispatch(clearCoursesFromEntityListData());
        })
        .finally(() => {
          setRequestGetAllCoursesFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [],
  );

  const requestAddCourseToEntity = useCallback(
    async (params: AddCourseToEntityUseCaseInterface.Params) => {
      setRequestAddCourseToEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .addCourseToEntity(params)
        .then((res) => {
          notifySuccess(res.message);
          requestGetAllCoursesFromEntity({ entityId });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestAddCourseToEntityStatus(STATUSPAGE.DONE);
        });
    },
    [entityId],
  );

  const requestUnlinkCourseFromEntity = useCallback(
    async (params: UnlinkCourseFromEntityUseCaseInterface.Params) => {
      setRequestUnlinkCourseFromEntityStatus(STATUSPAGE.LOADING);
      await entitiesService
        .unlinkCourseFromEntity(params)
        .then((res) => {
          notifySuccess(res.message);
          toggleUnlinkModal();
          requestGetAllCoursesFromEntity({ entityId });
        })
        .catch((err) => {
          notifyError(err.message);
        })
        .finally(() => {
          setRequestUnlinkCourseFromEntityStatus(STATUSPAGE.DONE);
        });
    },
    [entityId],
  );

  // Form Validation
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      course: null,
    },
    validationSchema: Yup.object({
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .required('Campo é obrigatório'),
    }),
    onSubmit: (values) => {
      requestAddCourseToEntity({ courseId: values.course?.value, entityId });
      validation.resetForm();
    },
  });

  useEffect(() => {
    requestGetAllCourses();
    requestGetAllCoursesFromEntity({ entityId });
  }, []);

  return (
    <Card className="mt-5">
      <CardBody>
        <Form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
            return false;
          }}
        >
          <Row>
            <Col>
              <Select
                isClearable
                id="course"
                name="course"
                placeholder="Selecione um Curso"
                onBlur={validation.handleBlur}
                value={validation?.values?.course}
                options={formatDataToLabelValue(
                  coursesSelectOptions as never,
                  'id',
                  'nome',
                )}
                onChange={(e) => {
                  validation.handleChange(
                    convertEventSelectToTarget('course', e),
                  );
                }}
                isDisabled={requestGetAllCoursesStatus === STATUSPAGE.LOADING}
                isLoading={requestGetAllCoursesStatus === STATUSPAGE.LOADING}
                invalid={
                  !!(validation?.touched?.course && validation?.errors?.course)
                }
              />
              <FormFeedBack
                error={String(validation.errors?.course)}
                isInvalid={
                  !!(validation.touched?.course && validation.errors?.course)
                }
              />
            </Col>
            <Col sm={2} className="mb-3">
              <div className="d-flex justify-content-center">
                <MainButton
                  type="submit"
                  className="w-100"
                  isDisabled={
                    requestAddCourseToEntityStatus === STATUSPAGE.LOADING
                  }
                  isLoading={
                    requestAddCourseToEntityStatus === STATUSPAGE.LOADING
                  }
                >
                  Adicionar
                </MainButton>
              </div>
            </Col>
          </Row>
        </Form>

        <Row>
          <DataTable
            noDataTitle={noDataConfig.noDataTitle}
            noDataDescription={noDataConfig.noDataDescription}
            data={coursesFromEntityListData}
            actions={[
              {
                id: 1,
                name: 'settings',
                label: 'Configurar Curso',
                onPress: (course) => {
                  requestGetCourseSettingsFromEntity({ id: course.id });
                },
                isLoading: (course) => loadingCourseSettingsId === course.id,
                nameFeatherIcon: 'settings',
                classNameColorIcon: 'primary',
              },
              {
                id: 2,
                name: 'unlink',
                label: 'Desvincular',
                onPress: (course) => {
                  selectedRelationshipIdRef.current = course.id;
                  toggleUnlinkModal();
                },
                nameFeatherIcon: 'user-minus',
                classNameColorIcon: 'danger',
              },
            ]}
            columns={makeCoursesFromEntityListColumns()}
            defaultSortAsc
            defaultSortFieldId={1}
            defaultRowsPerPage={50}
            loading={
              requestGetAllCoursesFromEntityStatus === STATUSPAGE.LOADING
            }
            canSearch={false}
          />
        </Row>
        <ModalCourseSettings
          isOpen={isSettingsModalOpen}
          toggleModal={toggleSettingsModal}
          courseSettings={courseSettings}
          relationshipId={selectedRelationshipId}
          onUpdated={() => requestGetAllCoursesFromEntity({ entityId })}
        />
        <ModalConfirmAction
          isOpen={isUnlinkModalOpen}
          toggle={toggleUnlinkModal}
          loading={requestUnlinkCourseFromEntityStatus === STATUSPAGE.LOADING}
          modalTheme="danger"
          onConfirm={() => {
            requestUnlinkCourseFromEntity({
              id: selectedRelationshipIdRef.current,
            });
          }}
          title="Remover Curso"
          subtitle="Ao remover este curso, todas as equipes e estudantes vinculados serão automaticamente desvinculados, e todas as atividades futuras relacionadas a ele serão excluídas. Tem certeza de que deseja continuar?"
          actionBtnLabel="Remover"
        />
      </CardBody>
    </Card>
  );
};
